import { useEffect, useState } from "react";
import { adminApi, type AdminSubstitutionDetail } from "@/services/api/admin";
import { ApiErrorResponse } from "@/services/api/client";
import { teacherApi } from "@/services/api/teacher";
import { radarI18nDe } from "@/components/radar/i18n/de";
import type { StornoConfirm } from "./TalkRadarStorno";

function cancelRequestError(err: unknown): string {
  const messages = radarI18nDe.slideOver.cancelErrors;
  if (err instanceof ApiErrorResponse) {
    const detail = err.detail.toLowerCase();
    if (err.statusCode === 400) {
      if (detail.includes("cancelled")) return messages.alreadyCancelled;
      if (detail.includes("booked")) return messages.alreadyBooked;
      if (detail.includes("declined")) return messages.alreadyDeclined;
      return err.detail;
    }
    if (err.statusCode === 403) return messages.noPermission;
    if (err.statusCode === 404) return messages.notFound;
    return err.detail || messages.generic;
  }
  if (err instanceof Error) return err.message;
  return messages.generic;
}

function stornoErrorMessage(kind: StornoConfirm["kind"], err: unknown): string {
  if (kind === "request") return cancelRequestError(err);
  if (kind === "remind") {
    return err instanceof Error ? err.message : "Erinnerung fehlgeschlagen";
  }
  return err instanceof Error ? err.message : "Stornierung fehlgeschlagen";
}

export function useTalkRadarStorno(
  requestId: string | null,
  admin: AdminSubstitutionDetail | null,
  setAdmin: (next: AdminSubstitutionDetail) => void
) {
  const [confirm, setConfirm] = useState<StornoConfirm | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setConfirm(null);
    setNotice(null);
    setError(null);
  }, [requestId]);

  async function commit(message: string) {
    if (!confirm || !requestId) return;
    const asked = confirm;
    try {
      if (asked.kind === "lesson") {
        await adminApi.cancelSubstitutionLessons([asked.lessonId]);
      } else if (asked.kind === "request") {
        await adminApi.cancelSubstitution(requestId);
      } else {
        const lessonRequestId = admin?.lesson_request_id;
        if (!lessonRequestId) {
          setError("Keine Lektion gefunden");
          return;
        }
        await teacherApi.sendRadarReminder(
          [lessonRequestId],
          message.trim() || undefined
        );
      }
      const next = await adminApi.getSubstitutionDetail(requestId);
      setAdmin(next);
      if (asked.kind === "remind") setNotice("Erinnerung gesendet");
      setError(null);
      setConfirm(null);
    } catch (err) {
      setNotice(null);
      setError(stornoErrorMessage(asked.kind, err));
    }
  }

  return { confirm, setConfirm, notice, error, commit };
}
